import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useReducer,
} from "react";

type LoaderState = {
  isInitialized: boolean;
  isAppReady: boolean;
};

type LoaderAction =
  | { type: "SET_INITIALIZED"; payload: boolean }
  | { type: "SET_APP_READY"; payload: boolean };

const initialState: LoaderState = {
  isInitialized: false,
  isAppReady: false,
};

const LoaderContext = createContext<{
  loader: LoaderState;
  dispatch: React.Dispatch<LoaderAction>;
}>({
  loader: initialState,
  dispatch: () => null,
});

const loaderReducer = (
  state: LoaderState,
  action: LoaderAction,
): LoaderState => {
  switch (action.type) {
    case "SET_INITIALIZED":
      return { ...state, isInitialized: action.payload };
    case "SET_APP_READY":
      return { ...state, isAppReady: action.payload };
    default:
      return state;
  }
};

export const LoaderProvider = ({ children }: { children: ReactNode }) => {
  const [loader, dispatch] = useReducer(loaderReducer, initialState);

  const memoizedValue = useMemo(
    () => ({
      loader,
      dispatch,
    }),
    [loader, dispatch],
  );

  return (
    <LoaderContext.Provider value={memoizedValue}>
      {children}
    </LoaderContext.Provider>
  );
};

export const useLoader = () => useContext(LoaderContext);
